import React from 'react';
import { Link } from 'react-router-dom';
import { Users, ArrowRight, Sparkles } from 'lucide-react';
import plcImg from '../../assets/images/plc.jpg'
import evImg from '../../assets/images/evtechnology.jpg'
import autocad from '../../assets/images/autocad.jpg'
// PopularCourseCard Component
const PopularCourseCard = ({ img, title, subtitle, students, link, variant = 'slider' }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const cardStyle = {
    width: '100%',
    height: variant === 'slider' ? '380px' : '320px',
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: isHovered
      ? '0 20px 40px rgba(11, 49, 97, 0.15)'
      : '0 8px 24px rgba(11, 49, 97, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
    border: '1px solid rgba(11, 49, 97, 0.08)',
    background: '#ffffff',
  };

  const bgStyle = {
    position: 'absolute',
    inset: 0,
    backgroundImage: `url(${img})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: isHovered ? 'brightness(1.05)' : 'brightness(0.95)',
    transition: 'filter 0.3s ease',
  };

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(8, 20, 38, 0) 0%, rgba(8, 20, 38, 0.7) 60%, rgba(8, 20, 38, 0.95) 100%)',
    transition: 'background 0.3s ease',
  };

  const contentStyle = {
    position: 'relative',
    zIndex: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    height: '100%',
    padding: variant === 'slider' ? '24px' : '20px',
    color: '#fff',
  };

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    width: 'fit-content',
    background: 'rgba(253, 214, 74, 0.2)',
    backdropFilter: 'blur(10px)',
    color: '#fdd649',
    padding: '6px 12px',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 700,
    marginBottom: '12px',
    border: '1px solid rgba(253, 214, 74, 0.3)',
  };

  const studentsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.9rem',
    marginBottom: '12px',
    opacity: 0.95,
    fontWeight: 500,
  };

  const titleStyle = {
    margin: '0 0 8px 0',
    fontSize: variant === 'slider' ? '1.35rem' : '1.15rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#fff',
  };

  const subtitleStyle = {
    margin: '0 0 16px 0',
    fontSize: '0.95rem',
    lineHeight: 1.5,
    color: 'rgba(255, 255, 255, 0.9)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  const linkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#fdd649',
    fontWeight: 700,
    fontSize: '0.95rem',
    marginTop: 'auto',
    opacity: isHovered ? 1 : 0,
    transform: isHovered ? 'translateY(0)' : 'translateY(8px)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  return (
    <Link
      to={link || '#'}
      style={{ ...cardStyle, display: 'block', textDecoration: 'none' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={bgStyle} />
      <div style={overlayStyle} />

      <div style={contentStyle}>
        <div style={badgeStyle}>
          <Sparkles size={12} />
          Featured
        </div>

        {students && (
          <div style={studentsStyle}>
            <Users size={16} />
            <span>{students} Students</span>
          </div>
        )}

        <h3 style={titleStyle}>{title}</h3>
        <p style={subtitleStyle}>{subtitle}</p>

        <div style={linkStyle}>
          Learn More <ArrowRight size={18} />
        </div>
      </div>
    </Link>
  );
};

// Main PopularCoursesModule Component
export default function PopularCoursesModule() {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1281);

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1281);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll slider
  React.useEffect(() => {
    if (!isDesktop) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (items.length - 2));
    }, 4000);
    return () => clearInterval(interval);
  }, [isDesktop]);

  const items = [
    {
      img: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800",
      title: "CNC Operator Turning",
      subtitle: "Machine operation, tooling & practical training",
      students: "12K+",
      link: "/courseDetails/3"
    },
    {
      img: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800",
      title: "CNC Programmer",
      subtitle: "G-code, CAM & programming for modern CNC machines",
      students: "8K+",
      link: "/courseDetails/4"
    },

    {
      img: plcImg,
      title: "Industrial Automation (PLC)",
      subtitle: "PLC programming, industrial control systems & automation logic",
      students: "14K+",
      link: "/courseDetails/plc"
    },
    {
      img: evImg,
      title: "EV Technology",
      subtitle: "Electric vehicle systems, battery tech & service diagnostics",
      students: "11K+",
      link: "/courseDetails/ev"
    },
    {
      img: autocad,
      title: "AutoCAD",
      subtitle: "2D/3D drafting, design fundamentals & CAD project skills",
      students: "13K+",
      link: "/courseDetails/autocad"
    }
  ];


  const wrapStyle = {
    width: '100%',
    padding: '60px 0',
    background: 'linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)',
  };

  const containerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: isDesktop ? '0.85fr 1.15fr' : '1fr',
    gap: isDesktop ? '80px' : '40px',
    alignItems: 'space-between',
    padding: '0 24px',
  };

  const leftStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: isDesktop ? 'left' : 'center',
  };

  const eyebrowStyle = {
    color: '#0b3161',
    fontWeight: 700,
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginBottom: '12px',
  };

  const headingStyle = {
    fontFamily: "'Roboto Slab', serif",
    color: '#081426',
    fontSize: isDesktop ? '2.75rem' : '2.2rem',
    margin: '0 0 16px 0',
    lineHeight: 1.1,
    fontWeight: 700,
  };

  const descStyle = {
    color: '#475569',
    fontSize: '1.05rem',
    margin: '0 0 32px 0',
    lineHeight: 1.65,
    maxWidth: isDesktop ? '420px' : '100%',
  };

  const btnStyle = {
    background: 'linear-gradient(90deg, #fdd649, #f7c42d)',
    border: 'none',
    color: '#081426',
    fontWeight: 700,
    padding: '14px 32px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '1rem',
    boxShadow: '0 8px 20px rgba(250, 186, 40, 0.2)',
    transition: 'all 0.3s ease',
    alignSelf: isDesktop ? 'flex-start' : 'center',
  };

  const sliderWrapperStyle = {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    marginLeft: 'auto',
  };

  const sliderStyle = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    maxWidth: '100%',
  };

  const slidesContainerStyle = {
    display: 'flex',
    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: `translateX(-${currentSlide * (100 / 3)}%)`,
    width: '100%',
  };

  const slideStyle = {
    flex: '0 0 calc(33.333% - 11px)',
    maxWidth: 'calc(33.333% - 11px)',
    marginRight: '16px',
  };

  const paginationStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '24px',
  };

  const dotStyle = (active) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: active
      ? 'linear-gradient(90deg, #7c3aed, #a855f7)'
      : 'rgba(11, 49, 97, 0.2)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: active ? '0 4px 12px rgba(124, 58, 237, 0.3)' : 'none',
  });

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: window.innerWidth > 768
      ? 'repeat(auto-fill, minmax(220px, 1fr))'
      : 'repeat(2, 1fr)',
    gap: '16px',
  };

  return (
    <section style={wrapStyle}>
      <div style={containerStyle}>
        <div style={leftStyle}>
          <div style={eyebrowStyle}>Skills You'll Master</div>
          <h2 style={headingStyle}>Popular Courses</h2>
          <p style={descStyle}>
            Practical, industry-focused programs created to build hands-on skills and fast-track your career in emerging fields.
          </p>
          <button style={btnStyle}>
            Explore All Courses
          </button>
        </div>

        <div>
          {isDesktop ? (
            <div style={sliderWrapperStyle}>
              <div style={sliderStyle}>
                <div style={slidesContainerStyle}>
                  {items.map((item, idx) => (
                    <div key={idx} style={slideStyle}>
                      <PopularCourseCard
                        img={item.img}
                        title={item.title}
                        subtitle={item.subtitle}
                        students={item.students}
                        link={item.link}
                        variant="slider"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div style={paginationStyle}>
                {[...Array(items.length - 2)].map((_, idx) => (
                  <div
                    key={idx}
                    style={dotStyle(currentSlide === idx)}
                    onClick={() => setCurrentSlide(idx)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div style={gridStyle}>
              {items.map((item, idx) => (
                <PopularCourseCard
                  key={idx}
                  img={item.img}
                  title={item.title}
                  subtitle={item.subtitle}
                  students={item.students}
                  link={item.link}
                  variant="grid"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}